// IndexedDB wrapper. No external library — the schema is small and stable,
// and a hand-written wrapper keeps failure modes visible and easy to reason about.

import type { AppSettings, AudioBlobRecord, BandRecord, MetronomePresetRecord, SetlistRecord, SongRecord } from '../core/types';

const DB_NAME = 'pale-moonlight-player';
const DB_VERSION = 3;

const STORE_SONGS = 'songs';
const STORE_AUDIO = 'audioBlobs';
const STORE_SETLISTS = 'setlists';
const STORE_SETTINGS = 'settings';
const STORE_METRONOME_PRESETS = 'metronomePresets';
const STORE_BANDS = 'bands';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_SONGS)) {
        db.createObjectStore(STORE_SONGS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_AUDIO)) {
        db.createObjectStore(STORE_AUDIO, { keyPath: 'songId' });
      }
      if (!db.objectStoreNames.contains(STORE_SETLISTS)) {
        db.createObjectStore(STORE_SETLISTS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_SETTINGS)) {
        db.createObjectStore(STORE_SETTINGS, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORE_METRONOME_PRESETS)) {
        db.createObjectStore(STORE_METRONOME_PRESETS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_BANDS)) {
        db.createObjectStore(STORE_BANDS, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open database'));
    request.onblocked = () => reject(new Error('Database upgrade blocked by another open tab'));
  });
  return dbPromise;
}

function tx<T>(
  db: IDBDatabase,
  storeNames: string[],
  mode: IDBTransactionMode,
  fn: (stores: Record<string, IDBObjectStore>) => IDBRequest<T> | void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeNames, mode);
    const stores: Record<string, IDBObjectStore> = {};
    for (const name of storeNames) stores[name] = transaction.objectStore(name);
    let requestResult: T;
    transaction.oncomplete = () => resolve(requestResult);
    transaction.onerror = () => reject(transaction.error ?? new Error('Transaction failed'));
    transaction.onabort = () => reject(transaction.error ?? new Error('Transaction aborted'));
    const maybeRequest = fn(stores);
    if (maybeRequest) {
      maybeRequest.onsuccess = () => {
        requestResult = maybeRequest.result;
      };
    }
  });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Request failed'));
  });
}

export class Database {
  async getAllSongs(): Promise<SongRecord[]> {
    const db = await openDatabase();
    const transaction = db.transaction(STORE_SONGS, 'readonly');
    const store = transaction.objectStore(STORE_SONGS);
    return requestToPromise(store.getAll());
  }

  async getSong(id: string): Promise<SongRecord | undefined> {
    const db = await openDatabase();
    const transaction = db.transaction(STORE_SONGS, 'readonly');
    return requestToPromise(transaction.objectStore(STORE_SONGS).get(id));
  }

  async putSong(song: SongRecord): Promise<void> {
    const db = await openDatabase();
    await tx(db, [STORE_SONGS], 'readwrite', (s) => s[STORE_SONGS].put(song));
  }

  async deleteSong(id: string): Promise<void> {
    const db = await openDatabase();
    await tx(db, [STORE_SONGS, STORE_AUDIO], 'readwrite', (s) => {
      s[STORE_SONGS].delete(id);
      s[STORE_AUDIO].delete(id);
    });
  }

  async putAudioBlob(record: AudioBlobRecord): Promise<void> {
    const db = await openDatabase();
    await tx(db, [STORE_AUDIO], 'readwrite', (s) => s[STORE_AUDIO].put(record));
  }

  async getAudioBlob(songId: string): Promise<AudioBlobRecord | undefined> {
    const db = await openDatabase();
    const transaction = db.transaction(STORE_AUDIO, 'readonly');
    return requestToPromise(transaction.objectStore(STORE_AUDIO).get(songId));
  }

  async getAllSetlists(): Promise<SetlistRecord[]> {
    const db = await openDatabase();
    const transaction = db.transaction(STORE_SETLISTS, 'readonly');
    return requestToPromise(transaction.objectStore(STORE_SETLISTS).getAll());
  }

  async putSetlist(setlist: SetlistRecord): Promise<void> {
    const db = await openDatabase();
    await tx(db, [STORE_SETLISTS], 'readwrite', (s) => s[STORE_SETLISTS].put(setlist));
  }

  async deleteSetlist(id: string): Promise<void> {
    const db = await openDatabase();
    await tx(db, [STORE_SETLISTS], 'readwrite', (s) => s[STORE_SETLISTS].delete(id));
  }

  async getSetting<K extends keyof AppSettings>(key: K): Promise<AppSettings[K] | undefined> {
    const db = await openDatabase();
    const transaction = db.transaction(STORE_SETTINGS, 'readonly');
    const record = await requestToPromise<{ key: string; value: unknown } | undefined>(
      transaction.objectStore(STORE_SETTINGS).get(key as string),
    );
    return record?.value as AppSettings[K] | undefined;
  }

  async setSetting<K extends keyof AppSettings>(key: K, value: AppSettings[K]): Promise<void> {
    const db = await openDatabase();
    await tx(db, [STORE_SETTINGS], 'readwrite', (s) => s[STORE_SETTINGS].put({ key, value }));
  }

  async getAllMetronomePresets(): Promise<MetronomePresetRecord[]> {
    const db = await openDatabase();
    const transaction = db.transaction(STORE_METRONOME_PRESETS, 'readonly');
    return requestToPromise(transaction.objectStore(STORE_METRONOME_PRESETS).getAll());
  }

  async putMetronomePreset(preset: MetronomePresetRecord): Promise<void> {
    const db = await openDatabase();
    await tx(db, [STORE_METRONOME_PRESETS], 'readwrite', (s) => s[STORE_METRONOME_PRESETS].put(preset));
  }

  async deleteMetronomePreset(id: string): Promise<void> {
    const db = await openDatabase();
    await tx(db, [STORE_METRONOME_PRESETS], 'readwrite', (s) => s[STORE_METRONOME_PRESETS].delete(id));
  }

  async getAllBands(): Promise<BandRecord[]> {
    const db = await openDatabase();
    const transaction = db.transaction(STORE_BANDS, 'readonly');
    return requestToPromise(transaction.objectStore(STORE_BANDS).getAll());
  }

  async putBand(band: BandRecord): Promise<void> {
    const db = await openDatabase();
    await tx(db, [STORE_BANDS], 'readwrite', (s) => s[STORE_BANDS].put(band));
  }

  async deleteBand(id: string): Promise<void> {
    const db = await openDatabase();
    await tx(db, [STORE_BANDS], 'readwrite', (s) => s[STORE_BANDS].delete(id));
  }

  async clearAllData(): Promise<void> {
    const db = await openDatabase();
    await tx(
      db,
      [STORE_SONGS, STORE_AUDIO, STORE_SETLISTS, STORE_SETTINGS, STORE_METRONOME_PRESETS, STORE_BANDS],
      'readwrite',
      (s) => {
        s[STORE_SONGS].clear();
        s[STORE_AUDIO].clear();
        s[STORE_SETLISTS].clear();
        s[STORE_SETTINGS].clear();
        s[STORE_METRONOME_PRESETS].clear();
        s[STORE_BANDS].clear();
      },
    );
  }
}

export const database = new Database();
